import { DITypes } from "@/core/@types/types";
import { diContainer } from "@/core/di";
import type { IFinancasGateway } from "@/core/services/financas/gateways/FinancasGateway";
import { defineStore } from "pinia";
import type { FinancialAccount, FinancialCompany } from "./types";

const allAccountsScope = "all";
const companiesLoadError = "Não foi possível carregar as empresas.";
const accountsLoadError = "Não foi possível carregar as contas.";
let companiesRequest: Promise<FinancialCompany[]> | undefined;
const accountRequests = new Map<string, Promise<FinancialAccount[]>>();

const buildAccountScope = (companyIds: string[]) =>
  companyIds.length
    ? [...companyIds]
        .sort((first, second) => first.localeCompare(second))
        .join(",")
    : allAccountsScope;

export const useFinancialCatalogStore = defineStore("financialCatalog", {
  state: () => ({
    companies: [] as FinancialCompany[],
    accountsByScope: {} as Record<string, FinancialAccount[]>,
    companiesLoaded: false,
    loadedAccountScopes: [] as string[],
    loadingCompanies: false,
    loadingAccountScopes: [] as string[],
    companiesError: null as string | null,
    accountsError: null as string | null
  }),
  getters: {
    allAccounts: (state): FinancialAccount[] =>
      state.accountsByScope[allAccountsScope] ?? []
  },
  actions: {
    invalidateCompanies() {
      companiesRequest = undefined;
      this.companies = [];
      this.companiesLoaded = false;
      this.loadingCompanies = false;
      this.companiesError = null;
    },
    invalidateAccounts() {
      accountRequests.clear();
      this.accountsByScope = {};
      this.loadedAccountScopes = [];
      this.loadingAccountScopes = [];
      this.accountsError = null;
    },
    getGateway() {
      return diContainer.get<IFinancasGateway>(DITypes.FINANCAS_AUTH);
    },
    async loadCompanies(force = false): Promise<FinancialCompany[]> {
      if (this.companiesLoaded && !force) return this.companies;
      if (companiesRequest && !force) return companiesRequest;

      this.loadingCompanies = true;
      this.companiesError = null;
      const request = this.getGateway()
        .getEmpresas()
        .then((response) => {
          // Uma resposta sem Data é falha de carregamento, não catálogo vazio:
          // marcá-la como carregada congelaria "Sem dados" em cache.
          if (!Array.isArray(response?.Data))
            throw new Error(companiesLoadError);

          const companies = response.Data.map((company) => ({
            id: company._id,
            name: company.Nome,
            brand: company.Marca
          }));
          if (companiesRequest === request) {
            this.companies = companies;
            this.companiesLoaded = true;
          }

          return companies;
        })
        .catch((error) => {
          if (companiesRequest === request) {
            this.companiesError = companiesLoadError;
          }

          throw error;
        });
      companiesRequest = request;

      try {
        return await request;
      } finally {
        if (companiesRequest === request) {
          this.loadingCompanies = false;
          companiesRequest = undefined;
        }
      }
    },
    async loadAccounts(
      companyIds: string[] = [],
      force = false
    ): Promise<FinancialAccount[]> {
      const scope = buildAccountScope(companyIds);

      if (this.loadedAccountScopes.includes(scope) && !force) {
        return this.accountsByScope[scope] ?? [];
      }
      const pendingRequest = accountRequests.get(scope);
      if (pendingRequest && !force) return pendingRequest;

      if (!this.loadingAccountScopes.includes(scope)) {
        this.loadingAccountScopes.push(scope);
      }
      this.accountsError = null;
      const request = this.getGateway()
        .getAccounts(companyIds)
        .then((response) => {
          // Mesmo critério das empresas: sem Data é falha, não escopo vazio.
          if (!Array.isArray(response?.Data))
            throw new Error(accountsLoadError);

          const accounts = response.Data.map((account) => ({
            id: account._id,
            name: account.Nome
          }));

          if (accountRequests.get(scope) === request) {
            this.accountsByScope[scope] = accounts;
            if (!this.loadedAccountScopes.includes(scope)) {
              this.loadedAccountScopes.push(scope);
            }
          }

          return accounts;
        })
        .catch((error) => {
          if (accountRequests.get(scope) === request) {
            this.accountsError = accountsLoadError;
          }

          throw error;
        });
      accountRequests.set(scope, request);

      try {
        return await request;
      } finally {
        if (accountRequests.get(scope) === request) {
          accountRequests.delete(scope);
          this.loadingAccountScopes = this.loadingAccountScopes.filter(
            (loadingScope) => loadingScope !== scope
          );
        }
      }
    },
    async getAccountNames(ids: string[]): Promise<string[]> {
      if (!ids.length) return [];

      const accounts = Object.values(this.accountsByScope).flat();
      const accountNames = new Map(
        accounts.map((account) => [account.id, account.name])
      );
      const missingIds = ids.filter((id) => !accountNames.has(id));

      if (missingIds.length) {
        return (await this.getGateway().getAccountNames(ids)) ?? [];
      }

      return ids
        .map((id) => accountNames.get(id))
        .filter((name): name is string => Boolean(name));
    }
  }
});

export { buildAccountScope };
